// Types
import type {
  TNewNote,
  TNote,
  TCallSummaryPayload,
  TSuppressPayload,
  THidePayload,
  TPropertyTimeline,
  TTalkingPointsRequestPayload,
  TTalkingPointsResponse,
  TEntityStatus,
} from '~/misc/opportunity/types';
import type { TApiHttpClientRequestConfig, TPaginationRequest } from '~/misc/resource/types';
import type { TContact, TContactActionPayload } from '~/misc/contact/types';

// Models
import { DefaultResource } from '~/misc/resource/default-resource.model';

export class OpportunityModel extends DefaultResource {
  notes(
    opportunityId: number,
    payload: TPaginationRequest,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TNote[]> {
    return this.get<TNote[]>(`opportunities/${opportunityId}/notes`, payload, requestConfig);
  }

  creatNotes(
    opportunityId: number,
    payload: TNewNote,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TNote> {
    return this.post<TNote>(`opportunities/${opportunityId}/notes`, payload, requestConfig);
  }

  public action(
    id: number,
    payload: TCallSummaryPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`opportunity/${id}/action`, payload, requestConfig);
  }

  public suppress(
    id: number,
    payload: TSuppressPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`opportunity/${id}/suppress`, payload, requestConfig);
  }

  public hide(
    id: number,
    payload: THidePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`opportunity/${id}/hide`, payload, requestConfig);
  }

  public hideAll(
    feature: string,
    status: string,
    modelId: number,
    payload: THidePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(
      `opportunities/${feature}/status/${status}/${modelId}/hide-all`,
      payload,
      requestConfig
    );
  }

  public hideAllByProperty(
    propertyId: number,
    payload: THidePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(
      `property/${propertyId}/opportunities/hide-all`,
      payload,
      requestConfig
    );
  }

  public markDoNotContact(
    opportunityId: number,
    contactId: number,
    payload: TContactActionPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TContact> {
    return this.post<TContact>(
      `opportunity/${opportunityId}/contact/${contactId}/mark-do-not-contact`,
      payload,
      requestConfig
    );
  }

  public propertyTimeline(
    id: number,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TPropertyTimeline[]> {
    return this.get<TPropertyTimeline[]>(
      `opportunity/${id}/property-timeline`,
      undefined,
      requestConfig
    );
  }

  talkingPoints(
    opportunityId: number,
    payload: TTalkingPointsRequestPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TTalkingPointsResponse> {
    return this.get<TTalkingPointsResponse>(
      `opportunity/${opportunityId}/talking-points`,
      payload,
      requestConfig
    );
  }

  public entityStatuses(requestConfig?: TApiHttpClientRequestConfig): Promise<TEntityStatus[]> {
    return this.get<TEntityStatus[]>('opportunity/entity-statuses', undefined, requestConfig);
  }
}
